import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  TableSortLabel,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as CsvIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
  Computer,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import moment from 'moment-timezone';

interface IdleSummaryFilters {
  startDate: string;
  endDate: string;
  userId?: string;
  deviceId?: string;
}

interface IdleSummaryRecord {
  userId: string;
  userName: string;
  email: string;
  deviceId: string;
  deviceName: string | null;
  date: string;
  idleMinutes: number;
  activeMinutes: number;
  totalMinutes: number;
  idlePercentage: number;
  daysTracked: number;
}

const IdleSummaryReportPage: React.FC = () => {
  const { user } = useAuth();
  const { trackingEnabled } = useSettings();
  const [filters, setFilters] = useState<IdleSummaryFilters>({
    startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  });
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<'userName' | 'date' | 'idleMinutes' | 'idlePercentage'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Check if user has permission
  const isAdministratorOrAbove = user?.role && ['administrator', 'super_user'].includes(user.role);

  // Fetch users for filter
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiClient.users.getAll(),
    enabled: isAdministratorOrAbove,
  });

  // Fetch devices for filter
  const { data: devicesData } = useQuery({
    queryKey: ['devices'],
    queryFn: () => apiClient.devices.getAll({ isActive: true }),
    enabled: isAdministratorOrAbove && trackingEnabled,
  });

  // Fetch idle summary data
  const { data: summaryData, isLoading, error, refetch } = useQuery({
    queryKey: ['idleSummary', filters],
    queryFn: async () => {
      const response = await apiClient.activityTracking.getIdleSummary({
        startDate: filters.startDate,
        endDate: filters.endDate,
        userId: filters.userId,
        deviceId: filters.deviceId,
      });
      return response.data;
    },
    enabled: isAdministratorOrAbove && trackingEnabled && !!filters.startDate && !!filters.endDate,
  });

  const handleFilterChange = (field: keyof IdleSummaryFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
    });
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportCSV = () => {
    if (!summaryData?.data) return;

    const csvRows: string[] = [];
    csvRows.push('Date,Employee Name,Email,Device ID,Device Name,Active Minutes,Idle Minutes,Total Minutes,Idle %');

    summaryData.data.forEach((record: IdleSummaryRecord) => {
      csvRows.push([
        record.date,
        record.userName || 'N/A',
        record.email || 'N/A',
        record.deviceId,
        record.deviceName || 'Unnamed',
        record.activeMinutes.toString(),
        record.idleMinutes.toString(),
        record.totalMinutes.toString(),
        record.idlePercentage.toString() + '%',
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `idle-summary-${filters.startDate}-to-${filters.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleExportMenuClose();
  };

  const toggleSort = (col: 'userName' | 'date' | 'idleMinutes' | 'idlePercentage') => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir(col === 'idlePercentage' ? 'desc' : 'asc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!summaryData?.data) return [];
    const copy = [...summaryData.data];
    copy.sort((a, b) => {
      let av: any = '';
      let bv: any = '';
      switch (sortBy) {
        case 'userName':
          av = (a.userName || '').toLowerCase();
          bv = (b.userName || '').toLowerCase();
          break;
        case 'date':
          av = new Date(a.date).getTime();
          bv = new Date(b.date).getTime();
          break;
        case 'idleMinutes':
          av = a.idleMinutes;
          bv = b.idleMinutes;
          break;
        case 'idlePercentage':
          av = a.idlePercentage;
          bv = b.idlePercentage;
          break;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [summaryData?.data, sortBy, sortDir]);

  if (!isAdministratorOrAbove) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Idle Summary Report
        </Typography>
        <Alert severity="warning" sx={{ mt: 3 }}>
          Access denied. Only administrators and above can access this report.
        </Alert>
      </Container>
    );
  }

  if (!trackingEnabled) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Idle Summary Report
        </Typography>
        <Alert severity="info" sx={{ mt: 3 }}>
          Tracking is currently disabled. Enable tracking in Settings to view activity reports.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Idle Summary Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track employee activity and idle time
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowDropDownIcon />}
          onClick={handleExportMenuOpen}
          disabled={!summaryData?.data || summaryData.data.length === 0}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportMenuClose}
        >
          <MenuItem onClick={handleExportCSV}>
            <ListItemIcon>
              <CsvIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export as CSV</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterIcon />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={usersData?.data?.data || []}
                getOptionLabel={(option: any) => `${option.firstName} ${option.lastName} (${option.email})`}
                value={usersData?.data?.data?.find((u: any) => u.id === filters.userId) || null}
                onChange={(_, newValue: any) => handleFilterChange('userId', newValue?.id || '')}
                renderInput={(params) => <TextField {...params} label="Employee" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={devicesData?.data?.data || []}
                getOptionLabel={(option: any) => `${option.deviceName || option.deviceId} (${option.deviceId})`}
                value={devicesData?.data?.data?.find((d: any) => d.deviceId === filters.deviceId) || null}
                onChange={(_, newValue: any) => handleFilterChange('deviceId', newValue?.deviceId || '')}
                renderInput={(params) => <TextField {...params} label="Device" />}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="contained" onClick={() => refetch()}>
                  Apply Filters
                </Button>
                <Button variant="outlined" onClick={handleResetFilters}>
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load idle summary data. Please try again.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Idle Summary Data
          </Typography>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : sortedData.length === 0 ? (
            <Alert severity="info">No data available for the selected date range.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'date'}
                        direction={sortBy === 'date' ? sortDir : 'asc'}
                        onClick={() => toggleSort('date')}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'userName'}
                        direction={sortBy === 'userName' ? sortDir : 'asc'}
                        onClick={() => toggleSort('userName')}
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell align="right">Active (min)</TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'idleMinutes'}
                        direction={sortBy === 'idleMinutes' ? sortDir : 'asc'}
                        onClick={() => toggleSort('idleMinutes')}
                      >
                        Idle (min)
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Total (min)</TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'idlePercentage'}
                        direction={sortBy === 'idlePercentage' ? sortDir : 'asc'}
                        onClick={() => toggleSort('idlePercentage')}
                      >
                        Idle %
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((record, index) => (
                    <TableRow key={`${record.userId}-${record.deviceId}-${record.date}-${index}`} hover>
                      <TableCell>{moment(record.date).format('MMM D, YYYY')}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{record.userName || 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {record.email || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontFamily="monospace" fontSize="0.875rem">
                            {record.deviceId}
                          </Typography>
                          {record.deviceName && (
                            <Typography variant="caption" color="text.secondary">
                              {record.deviceName}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{record.activeMinutes}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={record.idleMinutes}
                          size="small"
                          color={record.idlePercentage > 50 ? 'error' : record.idlePercentage > 30 ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell align="right">{record.totalMinutes}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${record.idlePercentage}%`}
                          size="small"
                          color={record.idlePercentage > 50 ? 'error' : record.idlePercentage > 30 ? 'warning' : 'success'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default IdleSummaryReportPage;

